import { cn } from "../../../utils/cn.js";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover/Popover.js";
import { cva } from "class-variance-authority";
import { CaretDownIcon, CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import * as React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { startOfDay } from "date-fns";
import { DayPicker } from "react-day-picker";
//#region src/components/Calendar/Calendar.tsx
const CalendarContext = React.createContext({ showToday: true });
const NAV_BUTTON_CLASSES = cn("pointer-events-auto remove-global-styles inline-flex h-[24px] w-[24px] shrink-0 cursor-pointer items-center justify-center", "rounded-xs border-0 bg-transparent text-text-secondary", "hover:bg-background-neutral-secondary-hover hover:text-text-primary", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected");
const calendarVariants = cva("font-body rounded-sm", {
	defaultVariants: { variant: "elevated" },
	variants: { variant: {
		elevated: cn("bg-elevation-surface-overlay", "border border-(length:--border-width-default) border-border-primary", "p-lg shadow-elevation-overlay"),
		flat: "p-lg"
	} }
});
/**
* Curri-styled date picker built on react-day-picker.
*
* Renders a month-view calendar with Curri design tokens, Phosphor chevron
* icons, and custom day-button styling that supports single-date and
* date-range selections. Outside-month days are shown with disabled text by
* default. Range selections render a continuous colored band across day cells
* with bold endpoints and subtle midpoints.
*
* @param props - Calendar configuration and react-day-picker props
* @returns The rendered calendar component
*/
function Calendar({ className, classNames, components, fixedWeeks = false, modifiers, modifiersClassNames, showOutsideDays = true, showToday = true, variant = "elevated", ...props }) {
	const calendarContext = React.useMemo(() => ({ showToday }), [showToday]);
	return /* @__PURE__ */ jsx(CalendarContext.Provider, {
		value: calendarContext,
		children: /* @__PURE__ */ jsx(DayPicker, {
			fixedWeeks,
			showOutsideDays,
			className: cn(calendarVariants({ variant }), "group/calendar data-[disabled]:bg-background-disabled data-[disabled]:border-border-disabled data-[disabled]:text-text-disabled", className),
			"data-disabled": props.disabled === true || void 0,
			classNames: {
				root: "relative isolate",
				months: "relative flex gap-lg",
				month: "flex-1",
				nav: "pointer-events-none absolute inset-x-0 top-0 z-10 flex h-[26px] items-center justify-between",
				button_previous: cn(NAV_BUTTON_CLASSES, "disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-text-disabled disabled:hover:bg-transparent disabled:hover:text-text-disabled", "group-data-[disabled]/calendar:pointer-events-none group-data-[disabled]/calendar:cursor-not-allowed group-data-[disabled]/calendar:text-text-disabled group-data-[disabled]/calendar:hover:bg-transparent group-data-[disabled]/calendar:hover:text-text-disabled"),
				button_next: cn(NAV_BUTTON_CLASSES, "disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-text-disabled disabled:hover:bg-transparent disabled:hover:text-text-disabled", "group-data-[disabled]/calendar:pointer-events-none group-data-[disabled]/calendar:cursor-not-allowed group-data-[disabled]/calendar:text-text-disabled group-data-[disabled]/calendar:hover:bg-transparent group-data-[disabled]/calendar:hover:text-text-disabled"),
				month_caption: "flex h-[26px] w-full items-center justify-center",
				caption_label: cn("font-body text-body-small leading-body-small font-heading-medium text-text-primary", "group-data-[disabled]/calendar:text-text-disabled"),
				month_grid: "mt-md w-full border-collapse",
				weekdays: "flex",
				weekday: cn("flex h-[28px] flex-1 items-center justify-center text-body-xsmall leading-body-xsmall font-heading-medium uppercase tracking-[0.25px] text-text-tertiary", "group-data-[disabled]/calendar:text-text-disabled"),
				week: "mt-xxs flex",
				day: "flex h-[36px] flex-1 items-center justify-center p-0",
				day_button: "",
				selected: "",
				outside: "",
				disabled: "cursor-not-allowed",
				hidden: "invisible",
				range_start: cn("bg-background-selected-bold rounded-l-xs", "has-[button:disabled]:bg-background-disabled", "group-data-[disabled]/calendar:bg-background-disabled"),
				range_end: cn("bg-background-selected-bold rounded-r-xs", "has-[button:disabled]:bg-background-disabled", "group-data-[disabled]/calendar:bg-background-disabled"),
				range_middle: cn("bg-background-brand-subtle-pressed", "has-[button:disabled]:bg-background-disabled", "group-data-[disabled]/calendar:bg-background-disabled"),
				...classNames
			},
			components: {
				DayButton: CalendarDayButton,
				Chevron: CalendarChevron,
				...components
			},
			modifiers,
			modifiersClassNames,
			...props
		})
	});
}
/**
* Internal chevron icon used by the calendar navigation buttons.
*
* Renders a Phosphor caret icon whose direction matches the provided
* `orientation`. Falls back to a downward caret for unrecognised values.
*
* @param props - Chevron props from react-day-picker
* @returns The rendered chevron icon
*/
function CalendarChevron({ className, orientation, ...iconProps }) {
	const classes = cn("h-[14px] w-[14px]", className);
	switch (orientation) {
		case "left": return /* @__PURE__ */ jsx(CaretLeftIcon, {
			className: classes,
			...iconProps
		});
		case "right": return /* @__PURE__ */ jsx(CaretRightIcon, {
			className: classes,
			...iconProps
		});
		default: return /* @__PURE__ */ jsx(CaretDownIcon, {
			className: classes,
			...iconProps
		});
	}
}
const TODAY_DOT_CLASSES = cn("relative", "after:absolute after:bottom-[5px] after:left-1/2 after:-translate-x-1/2", "after:h-[4px] after:w-[4px] after:rounded-full after:content-['']");
/**
* Custom day button used internally by the Calendar.
*
* Applies rounded corners, background colors, and today-dot indicator based
* on the current selection and modifier state. Automatically focuses the
* button when react-day-picker signals `modifiers.focused`.
*
* @param props - Day button props provided by react-day-picker
* @returns The rendered day button element
*/
function CalendarDayButton({ className, day, modifiers, ...props }) {
	const ref = React.useRef(null);
	const { showToday } = React.useContext(CalendarContext);
	React.useEffect(() => {
		if (modifiers.focused) ref.current?.focus();
	}, [modifiers.focused]);
	const isSelectedSingle = modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle;
	const isRangeEndpoint = modifiers.range_start || modifiers.range_end;
	const isRangeMiddle = !!modifiers.range_middle;
	const isDefault = !isSelectedSingle && !isRangeEndpoint && !isRangeMiddle;
	const isToday = showToday && !!modifiers.today && !modifiers.outside;
	const dayButtonProps = props;
	const isButtonDisabled = dayButtonProps.disabled === true;
	const defaultTextColor = isButtonDisabled || modifiers.disabled || modifiers.outside ? "text-text-disabled" : "text-text-primary";
	return /* @__PURE__ */ jsx("button", {
		ref,
		"data-day": day.date.toLocaleDateString(),
		"data-today": isToday || void 0,
		className: cn("remove-global-styles inline-flex h-[36px] w-[36px] cursor-pointer items-center justify-center border-0 bg-transparent", "text-body-small leading-body-small font-heading-medium outline-none", "disabled:cursor-not-allowed", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected", "group-data-[disabled]/calendar:cursor-not-allowed", isDefault && [
			"rounded-xs",
			!isButtonDisabled && !modifiers.disabled && "hover:bg-background-neutral-secondary-hover",
			defaultTextColor,
			modifiers.disabled && "cursor-not-allowed"
		], isSelectedSingle && [
			"rounded-xs",
			"bg-background-selected-bold text-text-brand-button hover:bg-background-selected-bold-hover",
			"disabled:bg-background-disabled disabled:text-text-disabled disabled:hover:bg-background-disabled",
			"group-data-[disabled]/calendar:bg-background-disabled group-data-[disabled]/calendar:text-text-disabled group-data-[disabled]/calendar:hover:bg-background-disabled"
		], isRangeEndpoint && [
			"rounded-xs",
			"text-text-brand-button hover:bg-background-selected-bold-hover",
			"disabled:bg-transparent disabled:text-text-disabled disabled:hover:bg-transparent",
			"group-data-[disabled]/calendar:bg-transparent group-data-[disabled]/calendar:text-text-disabled group-data-[disabled]/calendar:hover:bg-transparent"
		], isRangeMiddle && [
			"rounded-none",
			"text-text-primary hover:bg-background-brand-subtle-pressed",
			"disabled:bg-transparent disabled:text-text-disabled disabled:hover:bg-transparent",
			"group-data-[disabled]/calendar:bg-transparent group-data-[disabled]/calendar:text-text-disabled group-data-[disabled]/calendar:hover:bg-transparent"
		], isToday && [TODAY_DOT_CLASSES, isButtonDisabled ? "after:bg-text-disabled" : isSelectedSingle || isRangeEndpoint ? "after:bg-text-brand-button" : "after:bg-background-selected-bold"], className),
		...dayButtonProps
	});
}
/**
* Renders a Calendar inside a Popover with a customizable trigger.
*
* Provides a reusable "calendar-in-a-popover" primitive that handles
* open/close state, controlled/uncontrolled date selection, and
* defaultMonth reset on reopen. Accepts a render-prop `children` for the
* trigger so consumers can provide any trigger UI they need.
*
* @param props - Calendar popover configuration
* @returns The rendered calendar popover
*/
function CalendarPopover({ calendarProps, calendarClassName, children, defaultValue, disabled = false, onValueChange, popoverContentProps, showTodayButton = false, value }) {
	const { className: calPropClassName, defaultMonth: _calendarDefaultMonth, month: _calendarMonth, ...restCalendarProps } = calendarProps ?? {};
	const [internalValue, setInternalValue] = React.useState(defaultValue instanceof Date ? defaultValue : void 0);
	const [open, setOpen] = React.useState(false);
	const [calendarOpenCount, setCalendarOpenCount] = React.useState(0);
	const isControlled = value !== void 0;
	const selectedDate = isControlled ? value : internalValue;
	const calendarInitialMonth = selectedDate ?? /* @__PURE__ */ new Date();
	const handleOpenChange = React.useCallback((nextOpen) => {
		setOpen(nextOpen);
		if (nextOpen) setCalendarOpenCount((count) => count + 1);
	}, []);
	const handleSelect = React.useCallback((selected) => {
		if (!selected) return;
		if (!isControlled) setInternalValue(selected);
		onValueChange?.(selected);
		setOpen(false);
	}, [isControlled, onValueChange]);
	return /* @__PURE__ */ jsxs(Popover, {
		onOpenChange: handleOpenChange,
		open: disabled ? false : open,
		children: [/* @__PURE__ */ jsx(PopoverTrigger, {
			disabled,
			render: children(selectedDate, open)
		}), /* @__PURE__ */ jsxs(PopoverContent, {
			align: "center",
			side: "bottom",
			sideOffset: 2,
			...popoverContentProps,
			className: cn("min-w-0 items-center", popoverContentProps?.className),
			children: [/* @__PURE__ */ jsx(Calendar, {
				className: cn("!p-0", calPropClassName, calendarClassName),
				defaultMonth: calendarInitialMonth,
				mode: "single",
				onSelect: handleSelect,
				selected: selectedDate,
				variant: "flat",
				...restCalendarProps
			}, calendarOpenCount), showTodayButton && /* @__PURE__ */ jsx("button", {
				className: cn("remove-global-styles w-full cursor-pointer border-0 border-t border-t-(length:--border-width-default) border-solid border-border-primary", "bg-transparent px-lg py-sm", "text-body-small leading-body-small font-heading-medium text-text-brand", "hover:bg-background-neutral-secondary-hover", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected"),
				onClick: () => handleSelect(startOfDay(/* @__PURE__ */ new Date())),
				type: "button",
				children: "Today"
			})]
		})]
	});
}
//#endregion
export { Calendar, CalendarPopover, calendarVariants };

//# sourceMappingURL=Calendar.js.map